//  @flow
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Actions from "actions";
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardMedia,
  Grid, 
  Button, 
  CircularProgress,
  Chip,
  IconButton,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ImageIcon from '@mui/icons-material/Image';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CommentIcon from '@mui/icons-material/Comment';
import SyncIcon from '@mui/icons-material/Sync';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SortIcon from '@mui/icons-material/Sort';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InstagramIcon from '@mui/icons-material/Instagram';
import PeopleIcon from '@mui/icons-material/People';

import "./styles.scss";
import ButtonComponent from "../../../../Button";
import {openInstagram} from "../../../../../helpers/utils";

const AdminInstagramMedia = (props) => {
  const dispatch = useDispatch();
  const adminInstagram = useSelector(state => state.adminInstagram);

  const shopIdFromProps = props.match?.params?.shopId || props.shopId;

  const { onCancel } = props;

  // Load credentials from sessionStorage
  const getStoredCredentials = () => {
    try {
      const storedToken = sessionStorage.getItem('instagram_access_token');
      const storedAccountId = sessionStorage.getItem('instagram_system_account_id');
      return {
        token: storedToken || '',
        accountId: storedAccountId || ''
      };
    } catch (error) {
      console.error('Error loading stored credentials:', error);
      return { token: '', accountId: '' };
    }
  };

  const storedCreds = getStoredCredentials();

  // State management
  const [shopId] = useState(shopIdFromProps);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [downloading, setDownloading] = useState({});
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [accessToken, setAccessToken] = useState(storedCreds.token);
  const [systemAccountId, setSystemAccountId] = useState(storedCreds.accountId);
  const [mediaLimit, setMediaLimit] = useState(50);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [createProductsDialogOpen, setCreateProductsDialogOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('both');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addingVideoTo, setAddingVideoTo] = useState({});
  const [updateFollowersDialogOpen, setUpdateFollowersDialogOpen] = useState(false);

  // Initial data fetch
  useEffect(() => {
    if (shopId) {
      dispatch(Actions.adminGetInstagramMedia(shopId, currentPage, limit, filterType, sortOrder, mediaTypeFilter));
      dispatch(Actions.adminGetInstagramMediaStats(shopId));
    }
  }, [shopId, dispatch]);

  // Handle sync completion
  useEffect(() => {
    if (adminInstagram.syncResult && !adminInstagram.isSyncing) {
      const { media_saved } = adminInstagram.syncResult;
      const message = `Sync completed! New: ${media_saved.new}, Updated: ${media_saved.updated}, Failed: ${media_saved.failed}`;
      
      setShowSnackbar(true);
      setSnackbarMessage(message);
      setSnackbarSeverity('success');
      setSyncDialogOpen(false);

      // Refresh media list and stats
      dispatch(Actions.adminGetInstagramMedia(shopId, currentPage, limit, filterType, sortOrder, mediaTypeFilter));
      dispatch(Actions.adminGetInstagramMediaStats(shopId));
    }
  }, [adminInstagram.isSyncing, adminInstagram.syncResult]);

  // Handle product creation completion
  useEffect(() => {
    if (adminInstagram.createProductsResult && !adminInstagram.isCreatingProducts) {
      const { successful, failed, total } = adminInstagram.createProductsResult;
      const message = `Products created! Success: ${successful}/${total}, Failed: ${failed}`;
      
      setShowSnackbar(true);
      setSnackbarMessage(message);
      setSnackbarSeverity(successful > 0 ? 'success' : 'error');
      setCreateProductsDialogOpen(false);
      setSelectedMedia([]);
      setSelectAll(false);
    } else if (adminInstagram.serviceError && !adminInstagram.isCreatingProducts) {
      setShowSnackbar(true);
      setSnackbarMessage('Product creation failed. Please try again.');
      setSnackbarSeverity('error');
    }
  }, [adminInstagram.isCreatingProducts, adminInstagram.createProductsResult, adminInstagram.serviceError]);

  // Handle video added to product
  useEffect(() => {
    if (adminInstagram.addVideoResult && !adminInstagram.isAddingVideo) {
      setShowSnackbar(true);
      setSnackbarMessage('Video added to product successfully!');
      setSnackbarSeverity('success');
      setAddingVideoTo({});
      
      // Refresh media list to update product links
      dispatch(Actions.adminGetInstagramMedia(shopId, currentPage, limit, filterType, sortOrder, mediaTypeFilter));
    } else if (adminInstagram.serviceError && !adminInstagram.isAddingVideo) {
      setShowSnackbar(true);
      setSnackbarMessage('Failed to add video to product. Please try again.');
      setSnackbarSeverity('error');
      setAddingVideoTo({});
    }
  }, [adminInstagram.isAddingVideo, adminInstagram.addVideoResult]);

  // Handle update followers completion
  useEffect(() => {
    if (adminInstagram.updateFollowersResult && !adminInstagram.isUpdatingFollowers) {
      const result = adminInstagram.updateFollowersResult;
      const message = `Successfully updated! Followers: ${result.instagram_followers_count || 0}`;
      
      setShowSnackbar(true);
      setSnackbarMessage(message);
      setSnackbarSeverity('success');
      setUpdateFollowersDialogOpen(false);

      // Refresh stats to show updated data
      dispatch(Actions.adminGetInstagramMediaStats(shopId));
    } else if (adminInstagram.serviceError && !adminInstagram.isUpdatingFollowers) {
      setShowSnackbar(true);
      setSnackbarMessage('Failed to update followers. Please try again.');
      setSnackbarSeverity('error');
    }
  }, [adminInstagram.isUpdatingFollowers, adminInstagram.updateFollowersResult]);

  // Save Access Token to sessionStorage when it changes
  useEffect(() => {
    if (accessToken) {
      try {
        sessionStorage.setItem('instagram_access_token', accessToken);
      } catch (error) {
        console.error('Error saving access token:', error);
      }
    }
  }, [accessToken]);

  // Save System Account ID to sessionStorage when it changes
  useEffect(() => {
    if (systemAccountId) {
      try {
        sessionStorage.setItem('instagram_system_account_id', systemAccountId);
      } catch (error) {
        console.error('Error saving system account ID:', error);
      }
    }
  }, [systemAccountId]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    dispatch(Actions.adminGetInstagramMedia(shopId, page, limit, filterType, sortOrder, mediaTypeFilter));
  };

  const handleFilterChange = (event, newFilterType) => {
    setFilterType(newFilterType);
    setCurrentPage(1);
    setSelectedMedia([]);
    setSelectAll(false);
    dispatch(Actions.adminGetInstagramMedia(shopId, 1, limit, newFilterType, sortOrder, mediaTypeFilter));
  };

  const handleMediaTypeFilterChange = (event, newMediaTypeFilter) => {
    if (newMediaTypeFilter === null) return;
    
    setMediaTypeFilter(newMediaTypeFilter);
    setCurrentPage(1);
    setSelectedMedia([]);
    setSelectAll(false);
    dispatch(Actions.adminGetInstagramMedia(shopId, 1, limit, filterType, sortOrder, newMediaTypeFilter));
  };

  const handleSortChange = (event, newSortOrder) => {
    if (newSortOrder === null) return;
    
    setSortOrder(newSortOrder);
    setCurrentPage(1);
    setSelectedMedia([]);
    setSelectAll(false);
    dispatch(Actions.adminGetInstagramMedia(shopId, 1, limit, filterType, newSortOrder, mediaTypeFilter));
  };

  const handleOpenSyncDialog = () => {
    setSyncDialogOpen(true);
  };

  const handleCloseSyncDialog = () => {
    setSyncDialogOpen(false);
  };

  const handleSyncSubmit = () => {
    if (!accessToken || !systemAccountId) {
      setShowSnackbar(true);
      setSnackbarMessage('Please provide both Access Token and System Account ID');
      setSnackbarSeverity('error');
      return;
    }

    dispatch(Actions.adminSyncInstagramMedia(shopId, accessToken, systemAccountId, mediaLimit));
  };

  const handleClearCredentials = () => {
    try {
      sessionStorage.removeItem('instagram_access_token');
      sessionStorage.removeItem('instagram_system_account_id');
      setAccessToken('');
      setSystemAccountId('');
      setShowSnackbar(true);
      setSnackbarMessage('Credentials cleared from session');
      setSnackbarSeverity('info');
    } catch (error) {
      console.error('Error clearing credentials:', error);
    }
  };

  const handleOpenUpdateFollowersDialog = () => {
    setUpdateFollowersDialogOpen(true);
  };

  const handleCloseUpdateFollowersDialog = () => {
    setUpdateFollowersDialogOpen(false);
  };

  const handleUpdateFollowersSubmit = () => {
    if (!accessToken || !systemAccountId) {
      setShowSnackbar(true);
      setSnackbarMessage('Please provide both Access Token and System Account ID');
      setSnackbarSeverity('error');
      return;
    }

    dispatch(Actions.adminUpdateShopFollowers(shopId, accessToken, systemAccountId));
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  const handleMediaSelect = (media) => {
    const index = selectedMedia.findIndex(m => m.instagram_media_id === media.instagram_media_id);
    
    if (index > -1) {
      setSelectedMedia(selectedMedia.filter(m => m.instagram_media_id !== media.instagram_media_id));
      setSelectAll(false);
    } else {
      setSelectedMedia([...selectedMedia, media]);
    }
  };

  const handleSelectAll = (event) => {
    const { media } = adminInstagram.instagramMedia;
    
    if (event.target.checked) {
      setSelectedMedia([...media]);
      setSelectAll(true);
    } else {
      setSelectedMedia([]);
      setSelectAll(false);
    }
  };

  const handleSelectAllVideos = () => {
    const { media } = adminInstagram.instagramMedia;
    const videos = media.filter(item => item.media_type === 'VIDEO');
    setSelectedMedia(videos);
    setSelectAll(false);
  };

  const handleSelectAllImages = () => {
    const { media } = adminInstagram.instagramMedia;
    const images = media.filter(item => item.media_type === 'IMAGE' || item.media_type === 'CAROUSEL_ALBUM');
    setSelectedMedia(images);
    setSelectAll(false);
  };

  const handleSelectProduct = (media) => {
    if (!media.has_products || !media.product_ids || media.product_ids.length === 0) {
      setShowSnackbar(true);
      setSnackbarMessage('This media has no associated product. Create a product first.');
      setSnackbarSeverity('warning');
      return;
    }

    const productId = media.product_ids[0]._id || media.product_ids[0];
    
    setSelectedProduct({
      id: productId,
      media: media
    });
    setShowSnackbar(true);
    setSnackbarMessage('Product selected! Now click "Add Video" on any video to associate it.');
    setSnackbarSeverity('info');
  };

  const handleClearSelectedProduct = () => {
    setSelectedProduct(null);
    setShowSnackbar(true);
    setSnackbarMessage('Product selection cleared');
    setSnackbarSeverity('info');
  };

  const handleAddVideoToProduct = (videoMedia) => {
    if (!selectedProduct) {
      setShowSnackbar(true);
      setSnackbarMessage('Please select a product first by clicking "Select Product" on a photo.');
      setSnackbarSeverity('warning');
      return;
    }

    setAddingVideoTo({ ...addingVideoTo, [videoMedia.instagram_media_id]: true });

    dispatch(Actions.adminAddVideoToProduct(
      videoMedia.media_url,
      selectedProduct.id,
      videoMedia.instagram_media_id
    ));
  };

  const handleOpenCreateProductsDialog = () => {
    setCreateProductsDialogOpen(true);
  };

  const handleCloseCreateProductsDialog = () => {
    setCreateProductsDialogOpen(false);
  };

  const handleCreateProducts = () => {
    if (selectedMedia.length === 0) {
      setShowSnackbar(true);
      setSnackbarMessage('Please select at least one media item');
      setSnackbarSeverity('error');
      return;
    }

    dispatch(Actions.adminCreateProductsFromInstagram(shopId, selectedMedia));
  };

  const downloadMedia = async (media) => {
    const { instagram_media_id, media_url, media_type } = media;
    
    setDownloading({ ...downloading, [instagram_media_id]: true });

    try {
      const response = await fetch(media_url);
      const blob = await response.blob();
      
      const extension = media_type === 'VIDEO' ? 'mp4' : 'jpg';
      const filename = `instagram_${instagram_media_id}.${extension}`;
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download media');
    } finally {
      setDownloading({ ...downloading, [instagram_media_id]: false });
    }
  };

  const downloadSelected = async () => {
    if (!selectedMedia || selectedMedia.length === 0) {
      alert('No media selected');
      return;
    }

    for (const item of selectedMedia) {
      await downloadMedia(item);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const renderMediaCard = (media) => {
    const isDownloading = downloading[media.instagram_media_id];
    const isSelected = selectedMedia.some(m => m.instagram_media_id === media.instagram_media_id);
    const hasProducts = media.has_products || (media.product_ids && media.product_ids.length > 0);
    const isAddingVideo = addingVideoTo[media.instagram_media_id];
    const isSelectedProduct = selectedProduct && selectedProduct.media.instagram_media_id === media.instagram_media_id;
    const isVideo = media.media_type === 'VIDEO';
    
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={media._id}>
        <Card className={`media-card ${isSelected ? 'selected' : ''} ${hasProducts ? 'has-products' : ''}`}>
          <Box className="media-container">
            <Box className="media-checkbox">
              <Checkbox
                checked={isSelected}
                onChange={() => handleMediaSelect(media)}
                color="primary"
              />
            </Box>
            {media.media_type === 'VIDEO' ? (
              <video 
                src={media.media_url} 
                className="media-content"
                controls
              />
            ) : (
              <CardMedia
                component="img"
                image={media.media_url}
                alt={media.caption || 'Instagram media'}
                className="media-content"
              />
            )}
            <Box className="media-overlay">
              <Chip 
                icon={media.media_type === 'VIDEO' ? <VideoLibraryIcon /> : <ImageIcon />}
                label={media.media_type}
                size="small"
                color="primary"
              />
              {hasProducts && (
                <Chip 
                  icon={<AddShoppingCartIcon />}
                  label={`${media.product_ids?.length || 1} Product${media.product_ids?.length > 1 ? 's' : ''}`}
                  size="small"
                  color="success"
                  sx={{ ml: 0.5 }}
                />
              )}
            </Box>
          </Box>
          <CardContent>
            <Box className="engagement-stats">
              <Box className="stat-item">
                <FavoriteIcon fontSize="small" color="error" />
                <Typography variant="body2">{media.like_count || 0}</Typography>
              </Box>
              <Box className="stat-item">
                <CommentIcon fontSize="small" color="primary" />
                <Typography variant="body2">{media.comments_count || 0}</Typography>
              </Box>
            </Box>
            {media.caption && (
              <Typography 
                variant="body2" 
                className="media-caption"
                title={media.caption}
              >
                {media.caption.length > 60 ? `${media.caption.substring(0, 60)}...` : media.caption}
              </Typography>
            )}
            <Box className="media-actions">
              <Button
                variant="contained"
                size="small"
                startIcon={isDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={() => downloadMedia(media)}
                disabled={isDownloading}
                fullWidth
                sx={{ mb: 1 }}
              >
                {isDownloading ? 'Downloading...' : 'Download'}
              </Button>
              
              {!isVideo && hasProducts && (
                <Button
                  variant={isSelectedProduct ? "contained" : "outlined"}
                  size="small"
                  color={isSelectedProduct ? "success" : "primary"}
                  startIcon={<AddShoppingCartIcon />}
                  onClick={() => isSelectedProduct ? handleClearSelectedProduct() : handleSelectProduct(media)}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  {isSelectedProduct ? 'Selected ✓' : 'Select Product'}
                </Button>
              )}

              {isVideo && selectedProduct && (
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  startIcon={isAddingVideo ? <CircularProgress size={16} /> : <VideoLibraryIcon />}
                  onClick={() => handleAddVideoToProduct(media)}
                  disabled={isAddingVideo}
                  fullWidth
                >
                  {isAddingVideo ? 'Adding...' : 'Add to Product'}
                </Button>
              )}
            </Box>
            {media.timestamp && (
              <Typography variant="caption" color="textSecondary" className="media-date">
                {new Date(media.timestamp).toLocaleDateString()}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderStats = () => {
    const { statistics, instagram_followers_count, instagram_last_synced } = adminInstagram.instagramStats;

    if (!statistics) return null;

    const formatLastSyncDate = (date) => {
      if (!date) return 'Never synced';
      const syncDate = new Date(date);
      const now = new Date();
      const diffInHours = Math.floor((now - syncDate) / (1000 * 60 * 60));
      
      if (diffInHours < 1) return 'Less than an hour ago';
      if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
      
      return syncDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return (
      <Grid container spacing={2} className="stats-container">
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {statistics.total_media || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Media
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error">
                {statistics.total_likes || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Likes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {statistics.total_comments || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Comments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="secondary">
                {Math.round(statistics.avg_likes || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Avg Likes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {instagram_followers_count ? instagram_followers_count.toLocaleString() : '0'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Followers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTimeIcon color="action" />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Last Synced
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatLastSyncDate(instagram_last_synced)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const { instagramMedia, isLoading, isSyncing, isCreatingProducts } = adminInstagram;
  const { shop_name, instahandle, media, pagination } = instagramMedia;

  return (
    <Box className="admin-instagram-media">
      <Box className="header-section">
        <Box mb={2}>
          <ButtonComponent
              type="primary"
              variant="outlined"
              name="< Back"
              size="small"
              onClick={onCancel}
          />
        </Box>
        <Typography variant="h4" gutterBottom>
          Instagram Media
        </Typography>
        {shop_name && (
          <Box className="shop-info">
            <Typography variant="h6">{shop_name}</Typography>
            @{instahandle}
            {instahandle && <IconButton
                size="large"
                onClick={() => openInstagram(instahandle)}
            >
              <InstagramIcon sx={{width: 40, height: 40}} color="primary"/>
            </IconButton>}
          </Box>
        )}
      </Box>

      {renderStats()}

      <Box className="filter-section">
        <Box className="filter-label">
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1">Filter Media:</Typography>
        </Box>
        <ToggleButtonGroup
          value={filterType}
          exclusive
          onChange={handleFilterChange}
          aria-label="media filter"
          size="small"
        >
          <ToggleButton value="all" aria-label="all media">
            All Media
          </ToggleButton>
          <ToggleButton value="with_products" aria-label="with products">
            <CheckCircleIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
            With Products
          </ToggleButton>
          <ToggleButton value="without_products" aria-label="without products">
            Without Products
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box className="filter-section">
        <Box className="filter-label">
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1">Media Type:</Typography>
        </Box>
        <ToggleButtonGroup
          value={mediaTypeFilter}
          exclusive
          onChange={handleMediaTypeFilterChange}
          aria-label="media type filter"
          size="small"
        >
          <ToggleButton value="both" aria-label="both images and videos">
            All Types
          </ToggleButton>
          <ToggleButton value="images" aria-label="images only">
            <ImageIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
            Images Only
          </ToggleButton>
          <ToggleButton value="videos" aria-label="videos only">
            <VideoLibraryIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
            Videos Only
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box className="filter-section">
        <Box className="filter-label">
          <SortIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1">Sort By:</Typography>
        </Box>
        <ToggleButtonGroup
          value={sortOrder}
          exclusive
          onChange={handleSortChange}
          aria-label="sort order"
          size="small"
        >
          <ToggleButton value="newest" aria-label="newest first">
            <AccessTimeIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
            Newest First
          </ToggleButton>
          <ToggleButton value="oldest" aria-label="oldest first">
            Oldest First
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box className="actions-bar">
        <Box className="actions-left">
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAll}
                onChange={handleSelectAll}
                disabled={!media || media.length === 0}
              />
            }
            label="Select All"
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<VideoLibraryIcon />}
            onClick={handleSelectAllVideos}
            disabled={!media || media.length === 0}
            sx={{ ml: 1 }}
          >
            All Videos
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ImageIcon />}
            onClick={handleSelectAllImages}
            disabled={!media || media.length === 0}
            sx={{ ml: 1 }}
          >
            All Images
          </Button>
          {selectedMedia.length > 0 && (
            <Typography variant="body2" color="primary" sx={{ ml: 2 }}>
              {selectedMedia.length} selected
            </Typography>
          )}
        </Box>
        <Box className="actions-right">
          <Button
            variant="outlined"
            color="info"
            startIcon={adminInstagram.isUpdatingFollowers ? <CircularProgress size={16} /> : <PeopleIcon />}
            onClick={handleOpenUpdateFollowersDialog}
            disabled={adminInstagram.isUpdatingFollowers}
            sx={{ mr: 1 }}
          >
            {adminInstagram.isUpdatingFollowers ? 'Updating...' : 'Update Followers'}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={isCreatingProducts ? <CircularProgress size={16} /> : <AddShoppingCartIcon />}
            onClick={handleOpenCreateProductsDialog}
            disabled={selectedMedia.length === 0 || isCreatingProducts}
            sx={{ mr: 1 }}
          >
            {isCreatingProducts ? 'Creating...' : `Create Products (${selectedMedia.length})`}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleOpenSyncDialog}
            disabled={isSyncing}
            sx={{ mr: 1 }}
          >
            {isSyncing ? 'Syncing...' : 'Sync'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={downloadSelected}
            disabled={selectedMedia.length === 0 || isLoading}
          >
            Download Selected ({selectedMedia.length})
          </Button>
        </Box>
      </Box>

      {selectedProduct && (
        <Alert 
          severity="info" 
          onClose={handleClearSelectedProduct}
          sx={{ mb: 2 }}
        >
          <strong>Product Selected:</strong> Click "Add to Product" on any video to associate it with this product.
          {selectedProduct.media.caption && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              {selectedProduct.media.caption.substring(0, 100)}...
            </Typography>
          )}
        </Alert>
      )}

      {/* Sync Dialog */}
      <Dialog open={syncDialogOpen} onClose={handleCloseSyncDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Sync Instagram Media</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              You need Instagram API credentials to sync media. This will fetch up to {mediaLimit} recent posts from Instagram.
            </Alert>
            {(accessToken || systemAccountId) && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Credentials loaded from session storage. You can update them below or <Button size="small" onClick={handleClearCredentials}>clear saved credentials</Button>.
              </Alert>
            )}
            <TextField
              fullWidth
              label="Access Token"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              margin="normal"
              required
              helperText={accessToken ? "Using saved token from session" : ""}
            />
            <TextField
              fullWidth
              label="System Instagram Account ID"
              value={systemAccountId}
              onChange={(e) => setSystemAccountId(e.target.value)}
              margin="normal"
              required
              helperText={systemAccountId ? "Using saved account ID from session" : ""}
            />
            <TextField
              fullWidth
              label="Media Limit"
              type="number"
              value={mediaLimit}
              onChange={(e) => setMediaLimit(parseInt(e.target.value) || 50)}
              margin="normal"
              inputProps={{ min: 1, max: 100 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSyncDialog}>Cancel</Button>
          <Button 
            onClick={handleSyncSubmit} 
            variant="contained" 
            color="primary"
            disabled={isSyncing}
          >
            {isSyncing ? 'Syncing...' : 'Sync'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Followers Dialog */}
      <Dialog open={updateFollowersDialogOpen} onClose={handleCloseUpdateFollowersDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Instagram Followers Count</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              This will fetch and update the current followers count for this shop's Instagram account using the Business Discovery API.
            </Alert>
            {(accessToken || systemAccountId) && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Credentials loaded from session storage. You can update them below or <Button size="small" onClick={handleClearCredentials}>clear saved credentials</Button>.
              </Alert>
            )}
            <TextField
              fullWidth
              label="Access Token"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              margin="normal"
              required
              helperText={accessToken ? "Using saved token from session" : "Enter your Facebook/Instagram Access Token"}
            />
            <TextField
              fullWidth
              label="System Instagram Account ID"
              value={systemAccountId}
              onChange={(e) => setSystemAccountId(e.target.value)}
              margin="normal"
              required
              helperText={systemAccountId ? "Using saved account ID from session" : "Enter the Instagram Business Account ID to use for Business Discovery API"}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateFollowersDialog}>Cancel</Button>
          <Button 
            onClick={handleUpdateFollowersSubmit} 
            variant="contained" 
            color="primary"
            disabled={adminInstagram.isUpdatingFollowers}
            startIcon={adminInstagram.isUpdatingFollowers ? <CircularProgress size={16} /> : <PeopleIcon />}
          >
            {adminInstagram.isUpdatingFollowers ? 'Updating...' : 'Update Followers'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Products Dialog */}
      <Dialog open={createProductsDialogOpen} onClose={handleCloseCreateProductsDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create Products from Instagram Media</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              This will create a new product for each selected media item. The media will be downloaded from Instagram and uploaded to your Cloudinary account.
            </Alert>
            
            <Typography variant="h6" gutterBottom>
              Shop: {shop_name}
            </Typography>
            
            <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
              Selected Media ({selectedMedia.length}):
            </Typography>
            
            <List dense sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {selectedMedia.map((item, index) => (
                <React.Fragment key={item.instagram_media_id}>
                  <ListItem>
                    <ListItemText
                      primary={item.caption ? item.caption.substring(0, 50) + '...' : 'No caption'}
                      secondary={`${item.media_type} - ${item.like_count || 0} likes, ${item.comments_count || 0} comments`}
                    />
                  </ListItem>
                  {index < selectedMedia.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateProductsDialog}>Cancel</Button>
          <Button 
            onClick={handleCreateProducts} 
            variant="contained" 
            color="success"
            disabled={isCreatingProducts}
            startIcon={isCreatingProducts ? <CircularProgress size={16} /> : <AddShoppingCartIcon />}
          >
            {isCreatingProducts ? 'Creating...' : `Create ${selectedMedia.length} Products`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {isLoading ? (
        <Box className="loading-container">
          <CircularProgress />
        </Box>
      ) : media && media.length > 0 ? (
        <>
          <Grid container spacing={2} className="media-grid">
            {media.map(item => renderMediaCard(item))}
          </Grid>
          
          {pagination && pagination.total_pages > 1 && (
            <Box className="pagination-container">
              <Pagination
                count={pagination.total_pages}
                page={pagination.current_page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
              <Typography variant="body2" color="textSecondary" className="pagination-info">
                Showing {media.length} of {pagination.total} media items
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box className="empty-state">
          <Typography variant="h6" color="textSecondary">
            No Instagram media found for this shop
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AdminInstagramMedia;
